<template>
  <div>
    <div class="mb-3">
      <label class="form-label small">{{
        t("dashboard.portfolio.recipientCompanyName")
      }}</label>
      <SendRecipientPicker v-model="selection" :displayValue="aiCompanyName" />
      <div v-if="showNameRequired" class="form-text text-danger">
        {{ t("validations.required") }}
      </div>
    </div>
    <template v-if="!isHosted">
      <div class="mb-3">
        <label :for="accountInputId" class="form-label small">{{
          t("dashboard.portfolio.recipient_eecs_account_number")
        }}</label>
        <div class="input-group">
          <input
            :id="accountInputId"
            type="text"
            class="form-control form-control-sm"
            :class="{ 'is-invalid': accountMessageKey }"
            :value="model.eecsAccountNumber"
            :disabled="disabled"
            @input="
              updateRecipient({
                eecsAccountNumber: ($event.target as HTMLInputElement).value,
              })
            "
          />
          <span
            v-if="countryDisplay"
            class="input-group-text bg-light text-muted small"
          >
            {{ countryDisplay }}
          </span>
        </div>
        <div v-if="accountMessageKey" class="form-text text-danger">
          {{ t(accountMessageKey) }}
        </div>
        <EecsNameMismatchWarning
          :companyName="model.companyName"
          :accountNumber="model.eecsAccountNumber"
        />
      </div>
      <div v-if="showInternalAccount" class="mb-3">
        <label :for="internalAccountInputId" class="form-label small">{{
          t("dashboard.portfolio.recipient_internal_account_number")
        }}</label>
        <input
          :id="internalAccountInputId"
          type="text"
          class="form-control form-control-sm"
          :value="model.internalAccountNumber"
          :disabled="disabled"
          :placeholder="
            t('dashboard.portfolio.recipient_internal_account_placeholder')
          "
          @input="
            updateRecipient({
              internalAccountNumber: ($event.target as HTMLInputElement).value,
            })
          "
        />
        <div class="form-text text-muted">
          {{ t("dashboard.portfolio.recipient_internal_account_help") }}
        </div>
      </div>
      <slot name="external-fields" />
    </template>
    <div
      v-else
      class="alert alert-subtle-secondary d-flex align-items-start gap-2 mb-3"
    >
      <FontAwesomeIcon
        icon="fas fa-circle-info"
        class="mt-1 flex-shrink-0 text-muted"
      />
      <span class="small opacity-75">
        {{ t("go_send.hosted_delivery_info") }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Recipient fields of a GO send, shared by the Send GO tab and the PPA SEND action editor.
 *
 * Picking a hosted-account company hides the registry account fields (Soldera resolves the
 * destination per certificate) and clears any values typed into them. Any change in the picker
 * also drops the document-extracted name. Account errors show as soon as a value is entered;
 * missing values only after touchAndValidate().
 */
import EecsNameMismatchWarning from "@/components/common/EecsNameMismatchWarning.vue";
import {
  isHostedRecipient,
  isRecipientComplete,
  recipientAccountError,
  recipientCountryDisplay,
  type RecipientSelection,
  type SendRecipient,
} from "@/dashboard/inventory_management/send/sendRecipient";
import SendRecipientPicker from "@/dashboard/inventory_management/send/SendRecipientPicker.vue";
import type { RegistrationAuthority } from "@/utils/registryAccounts";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    /** Registration authority of the certificates being sent; null when unknown until execution (PPA). */
    authority: RegistrationAuthority | null;
    showInternalAccount?: boolean;
    disabled?: boolean;
  }>(),
  { showInternalAccount: true, disabled: false }
);

const model = defineModel<SendRecipient>({ required: true });
/** Company name read from an uploaded document; cleared once the user picks or types a recipient. */
const aiCompanyName = defineModel<string | undefined>("aiCompanyName");

const { t } = useI18n();
const accountInputId = useId();
const internalAccountInputId = useId();

const touched = ref(false);

const isHosted = computed(() => isHostedRecipient(model.value));

const countryDisplay = computed(() =>
  recipientCountryDisplay(model.value, props.authority)
);

const selection = computed<RecipientSelection>({
  get: () => ({
    companyId: model.value.companyId,
    companyName: model.value.companyName,
    eecsAccountNumber: model.value.eecsAccountNumber,
  }),
  set: (picked) => {
    model.value = {
      ...model.value,
      ...picked,
      internalAccountNumber:
        picked.companyId != null ? "" : model.value.internalAccountNumber,
    };
    aiCompanyName.value = undefined;
  },
});

const accountMessageKey = computed(() => {
  const error = recipientAccountError(model.value, props.authority);
  if (error) return error;
  return touched.value &&
    !isHosted.value &&
    !model.value.eecsAccountNumber.trim()
    ? "validations.required"
    : null;
});

const showNameRequired = computed(
  () => touched.value && !model.value.companyName.trim()
);

const updateRecipient = (changes: Partial<SendRecipient>) => {
  model.value = { ...model.value, ...changes };
};

const isValid = () => isRecipientComplete(model.value, props.authority);

const touchAndValidate = () => {
  touched.value = true;
  return isValid();
};

defineExpose({ isValid, touchAndValidate });
</script>
